import { z } from 'zod';

/**
 * Client-side validation, mirroring the Zod schemas in the backend.
 *
 * This is a UX layer, not a security boundary - the server validates every
 * request regardless. The point is that a typo is caught as the user leaves
 * the field, rather than after a round trip that loses their place in a long
 * form. Keep the rules in step with the Zod schemas under
 * windocs-backend/src/modules.
 */

const username = z
  .string()
  .trim()
  .min(3, 'At least 3 characters')
  .max(40, 'At most 40 characters')
  .regex(/^[a-z0-9._-]+$/, 'Lowercase letters, numbers, dot, underscore or hyphen only');

const email = z.email('Enter a valid email address').max(160, 'At most 160 characters');

const password = z
  .string()
  .min(8, 'Use at least 8 characters')
  .max(128, 'At most 128 characters')
  .regex(/[a-z]/, 'Include a lowercase letter')
  .regex(/[A-Z]/, 'Include an uppercase letter')
  .regex(/\d/, 'Include a number');

export const loginSchema = z.object({
  identifier: z.string().trim().min(1, 'Enter your username or email'),
  password: z.string().min(1, 'Enter your password'),
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: password,
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: 'Choose a password you have not used before',
    path: ['newPassword'],
  });

export const createTenantSchema = z.object({
  name: z.string().trim().min(2, 'Enter the tenant name').max(160, 'At most 160 characters'),
  industryId: z.string().min(1, 'Choose a industry'),
  tradeLicenseNo: z
    .string()
    .trim()
    .min(1, 'Enter the trade licence number')
    .max(60, 'At most 60 characters'),
  subscriptionExpiresAt: z
    .string()
    .min(1, 'Choose an expiry date')
    .refine((value) => new Date(value).getTime() > Date.now(), 'Must be a future date'),
  maxAdminUsers: z
    .number({ error: 'Enter a number' })
    .int()
    .min(1, 'At least 1')
    .max(500, 'At most 500'),
  maxClientUsers: z
    .number({ error: 'Enter a number' })
    .int()
    .min(1, 'At least 1')
    .max(5000, 'At most 5000'),
  address: z.string().trim().max(400, 'At most 400 characters'),
  remarks: z.string().trim().max(1000, 'At most 1000 characters'),
  adminUsername: username,
  adminEmail: email,
});

/** Editing a tenant leaves the administrator account alone. */
export const updateTenantSchema = createTenantSchema.omit({
  adminUsername: true,
  adminEmail: true,
});

export const industrySchema = z.object({
  name: z.string().trim().min(2, 'Enter the industry').max(60, 'At most 60 characters'),
});

export const updateUserSchema = z.object({
  name: z.string().trim().min(2, 'Enter the full name').max(120, 'At most 120 characters'),
  username,
  email,
});

export const resetPasswordSchema = z.object({ newPassword: password });

/**
 * Runs a schema and returns errors keyed by field, in the shape the form hook
 * and the `Field` component expect.
 */
export function collectErrors(
  schema: z.ZodType,
  values: unknown,
): { valid: boolean; errors: Record<string, string> } {
  const result = schema.safeParse(values);

  if (result.success) {
    return { valid: true, errors: {} };
  }

  const errors: Record<string, string> = {};

  for (const issue of result.error.issues) {
    const field = issue.path.join('.') || '_form';
    /* First error per field only - a stack of messages under one input is
       noise, and fixing the first usually clears the rest. */
    errors[field] ??= issue.message;
  }

  return { valid: false, errors };
}
